import React, { useCallback, useRef, useState } from 'react';
import usePaginatedItems from '../../../hooks/usePaginatedItems';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import {
  SaleOrder,
  SaleOrdersSearchBy,
  statusClassMap,
} from 'models/SaleOrder';
import { useParams } from 'react-router-dom';
import Table from 'router/subrouters/Dashboard/components/Table';
import { useTranslation } from 'react-i18next';
import useLabels from './hooks/useLabels';
import utils from 'utils';
import Modal, { ModalRef } from 'components/Modal';
import classNames from 'classnames';
import OrderDetails from './components/OrderDetails';

import './SaleOrders.styles.scss';

const SaleOrdersPage = () => {
  const [orderId, setOrderId] = useState<string>(null);
  const [displayExpired, setDisplayExpired] = useState(false);
  const { storeNo } = useParams();
  const labels = useLabels();

  const modalRef = useRef<ModalRef>();

  const { t } = useTranslation();

  const {
    params: { page, search, limit, searchBy },
  } = useQueryParams<{
    page: string;
    search: string;
    limit: string;
    searchBy: SaleOrdersSearchBy;
  }>({ searchBy: 'id' });

  const { items, isFetching, totalPages, totalItems } = usePaginatedItems({
    queryKey: 'sale-orders',
    makeRequest: api.saleOrders.getSaleOrders,
    searchParam: searchBy,
    params: {
      limit,
      page,
      [searchBy]: !!search && search.length >= 2 ? search : undefined,
      storeNo,
    },
  });

  const {
    items: expiredItems,
    isFetching: isFetchingExpired,
    totalPages: totalPagesExpired,
    totalItems: totalItemsExpired,
  } = usePaginatedItems({
    queryKey: 'sale-orders-expired',
    makeRequest: api.saleOrders.getExpiredOders,
    searchParam: searchBy,
    params: {
      limit,
      page,
      [searchBy]: !!search && search.length >= 2 ? search : undefined,
      storeNo,
    },
  });

  const renderContentArray = useCallback(
    (data: SaleOrder[]) =>
      data?.map((order) => {
        const {
          id,
          customerName,
          assignedAssociate,
          dueDate,
          orderStatus,
          itemCount,
          orderType,
          storeNo,
          items: orderItems,
        } = order;

        return {
          id,
          customerName,
          associate: (
            <span style={{ fontStyle: 'italic' }}>
              {assignedAssociate?.associateId || 'Unnassigned'}
            </span>
          ),
          items: orderItems,
          storeNo: +storeNo,
          assignedAssociate,
          dueDate: utils.formatISODate(dueDate),
          status: (
            <span
              className={classNames(
                'hhsa-sale-orders__status',
                `hhsa-sale-orders__status--${statusClassMap[orderStatus]}`,
              )}
            >
              {orderStatus}
            </span>
          ),
          orderStatus,
          itemCount,
          orderType,
        };
      }),
    [],
  );

  return (
    <>
      <Table
        title={t('SaleOrders', { storeNo: storeNo })}
        labels={labels}
        checkboxLabel={t('showExpired')}
        checked={displayExpired}
        className="hhsa-sale-orders"
        onCheckBox={() => setDisplayExpired((prev) => !prev)}
        contentArray={renderContentArray(displayExpired ? expiredItems : items)}
        onRowClick={(el) => {
          setOrderId(el?.id);
          modalRef.current.open();
        }}
        totalItems={displayExpired ? totalItemsExpired : totalItems}
        fetching={displayExpired ? isFetchingExpired : isFetching}
        totalPages={displayExpired ? totalPagesExpired : totalPages}
        elementProps={labels?.map(({ key }) => key)}
      />
      <Modal
        ref={modalRef}
        focusableElements="div"
        type="no-action"
        modalName="sale-order"
        onClose={() => {
          setOrderId(null);
        }}
        onX={() => {
          setOrderId(null);
          modalRef.current.close();
        }}
        title={t('SaleOrderDetails', { id: orderId })}
      >
        <OrderDetails id={orderId} />
      </Modal>
    </>
  );
};

export default SaleOrdersPage;
